import { SafeFormattedText } from '@/components/ui/safe-formatted-message'
import {
  translateMessagesFx,
  useMessageTranslation,
} from '@/models/chattingUI/messages/translations.ts'
import CurrencyCircleIcon from '@/modules/chatting/icons/icon-currency-dollar-circle.svg'
import type { Message } from '@/modules/chatting/types'

interface Props {
  message: Message
  searchedKeyword?: string
}

export const ChatMessageText = ({ message, searchedKeyword }: Props) => {
  const translation = useMessageTranslation(message.id)
  const { isTip, tipText, text = '' } = message
  const shouldShowOriginal =
    !!translation?.text || translation?.isLoading || translation?.isError

  const originalText = (
    <span className="mt-1 block gap-1 text-ellipsis text-xs text-secondary-300">
      <SafeFormattedText
        testId="message-text"
        className="overflow-hidden"
        text={
          text?.startsWith('<p>')
            ? text.substring(0, 3) + '<b>Original:</b> ' + text.substring(3)
            : '<b>Original:</b> ' + text
        }
        matchedKeywords={searchedKeyword}
      />
    </span>
  )

  const textView = translation?.isLoading ? (
    <div className="three-dot-loader w-[50px]" />
  ) : translation?.isError ? (
    <div className="flex items-center gap-2">
      <p className="text-danger-default text-sm">Failed to translate</p>
      <button
        onClick={() =>
          translateMessagesFx({ messageIds: [message.id], forceShow: true })
        }
        className="text-xs font-medium text-blue-600"
      >
        Retry
      </button>
    </div>
  ) : (
    <SafeFormattedText
      testId={translation?.text ? 'translated-message-text' : 'message-text'}
      text={translation?.text?.replaceAll('\n', '<br/>') || text}
      matchedKeywords={searchedKeyword}
    />
  )

  if (isTip) {
    return (
      <div>
        <div className="flex items-center rounded bg-primary-200 p-1 text-sm font-medium dark:bg-primary-800">
          <CurrencyCircleIcon className="h-5 w-5" />
          <p className="ml-1.5">{textView}</p>
        </div>
        {shouldShowOriginal && originalText}
        <p className="mt-1">
          <SafeFormattedText
            testId="tip-text"
            text={tipText ?? ''}
            matchedKeywords={searchedKeyword}
          />
        </p>
      </div>
    )
  }

  return (
    <>
      {textView}
      {shouldShowOriginal && originalText}
    </>
  )
}
